import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, User, MessageCircle, Clock, CheckCheck, Phone, Video, Mail, Bot } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'
import SEO from '../components/SEO'
import { db } from '../firebase'
import { collection, addDoc, query, where, orderBy, onSnapshot, serverTimestamp, getDocs, updateDoc, doc } from 'firebase/firestore'

const LiveChat = () => {
  const { t } = useLanguage()
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [agentStatus, setAgentStatus] = useState('bot') // Start with bot
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [showContactForm, setShowContactForm] = useState(true)
  const [sessionId, setSessionId] = useState(null)
  const [isHandoffOffered, setIsHandoffOffered] = useState(false)
  const [talkingToHuman, setTalkingToHuman] = useState(false)
  const messagesEndRef = useRef(null)
  const chatContainerRef = useRef(null)

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Prevent body scroll when chat is active
  useEffect(() => {
    if (!showContactForm && sessionId) {
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = 'unset'
      }
    }
  }, [showContactForm, sessionId])

  // Check for online staff members
  useEffect(() => {
    if (!sessionId || talkingToHuman) return

    const staffRef = collection(db, 'staff')
    const unsubscribe = onSnapshot(staffRef, async (snapshot) => {
      const onlineStaff = snapshot.docs.filter(doc => doc.data().isOnline === true)
      
      if (onlineStaff.length > 0 && !isHandoffOffered) {
        // An admin is online, offer handoff
        setIsHandoffOffered(true)
        setIsTyping(true)
        
        setTimeout(async () => {
          await addDoc(collection(db, 'chatMessages'), {
            sessionId,
            sender: 'bot',
            senderName: 'AI Asistent',
            text: `Një nga anëtarët e stafit tonë është online tani! 👨‍💼 Dëshiron të flasësh me ta për më shumë ndihmë dhe konsultim të personalizuar?`,
            timestamp: serverTimestamp(),
            read: false,
            isHandoffOffer: true
          })
          setIsTyping(false)
        }, 2000)
      }
    })

    return () => unsubscribe()
  }, [sessionId, isHandoffOffered, talkingToHuman])

  // Listen for messages in real-time when session is active
  useEffect(() => {
    if (!sessionId) return

    const messagesRef = collection(db, 'chatMessages')
    const q = query(
      messagesRef,
      where('sessionId', '==', sessionId)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const loadedMessages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        time: doc.data().timestamp?.toDate().toLocaleTimeString('sq-AL', { 
          hour: '2-digit', 
          minute: '2-digit' 
        }) || 'Tani',
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }))
      
      // Sort by timestamp on client side
      loadedMessages.sort((a, b) => a.timestamp - b.timestamp)
      setMessages(loadedMessages)
    })

    return () => unsubscribe()
  }, [sessionId])

  const handleStartChat = async (e) => {
    e.preventDefault()
    if (userName.trim() && userEmail.trim()) {
      try {
        // Generate session ID
        const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        setSessionId(newSessionId)
        
        // Create session document
        await addDoc(collection(db, 'chatSessions'), {
          sessionId: newSessionId,
          userName,
          userEmail,
          startedAt: serverTimestamp(),
          status: 'active',
          assignedTo: 'bot'
        })

        // Send welcome message from bot
        await addDoc(collection(db, 'chatMessages'), {
          sessionId: newSessionId,
          sender: 'bot',
          senderName: 'AI Asistent',
          text: `Përshëndetje ${userName}! 👋 Unë jam asistenti virtual i Contenta Marketing. Si mund t'ju ndihmoj sot?`,
          timestamp: serverTimestamp(),
          read: false
        })

        setShowContactForm(false)
        
        // Auto-respond with bot suggestions after 2 seconds
        setTimeout(async () => {
          await addDoc(collection(db, 'chatMessages'), {
            sessionId: newSessionId,
            sender: 'bot',
            senderName: 'AI Asistent',
            text: 'Mund të ju ndihmoj me:\n• Informacione për shërbimet tona\n• Çmime dhe paketa\n• Procesi i punës\n• Pyetje të shpeshta\n\nÇfarë ju intereson më shumë?',
            timestamp: serverTimestamp(),
            read: false
          })
        }, 2000)
      } catch (error) {
        console.error('Error starting chat:', error)
        alert('Ka ndodhur një gabim. Provoni përsëri ose na kontaktoni nëpërmjet WhatsApp.')
      }
    }
  }

  // SUPER INTELLIGENT Bot with Advanced NLP and Context Awareness
  const getBotResponse = (userMessage) => {
    const msg = userMessage.toLowerCase().trim()
    
    // Multi-language detection
    const isAlbanian = /[ë]/i.test(msg) || /\b(kam|është|për|çfarë|si|mund)\b/i.test(msg)
    const isEnglish = /\b(what|how|can|need|want|is|are)\b/i.test(msg) && !isAlbanian
    
    // Advanced sentiment & intent detection
    const positiveSentiment = /\b(faleminderit|falemnderit|thanks|thank you|excellent|mire|shkëlqyeshëm|super|perfect|love|awesome|great|amazing|fantastic|wonderful|happy|lumtur|i kënaqur|bravo)\b/i.test(msg)
    const negativeSentiment = /\b(keq|problem|issue|shqetësim|complaint|ankesë|nuk funksionon|doesn't work|error|gabim|issue|frustrated|i mërzitur|i zhgënjyer|e zhgënjyer|jo mirë|sucks|terrible|horrible)\b/i.test(msg)
    const urgentSentiment = /\b(urgent|emergjencë|emergency|shpejt|quickly|menjëherë|now|tani|immediately|asap|sa më shpejt|right now|kritik|critical)\b/i.test(msg)
    const confusedSentiment = /\b(confused|i hutuar|nuk kuptoj|don't understand|unclear|çfarë|what|explain|shpjego|help me understand)\b/i.test(msg)
    const interestedSentiment = /\b(interesuar|interested|curious|kureshtar|më intereson|më duhet|i need|kam nevojë|dua|want|looking for|po kërkoj)\b/i.test(msg)
    
    // Question type detection
    const isPricingQuestion = /\b(çmim|price|cost|kosto|buxhet|paketa|sa kushton|how much)\b/i.test(msg)
    const isTimelineQuestion = /\b(sa kohë|deadline|shpejt|when|kur|afat|time|sa zgjat)\b/i.test(msg)
    const isQualityQuestion = /\b(cilësi|quality|sa mirë|how good|profesional|professional)\b/i.test(msg)
    
    // Handle urgent requests first
    if (urgentSentiment) {
      return '🚨 **Kuptoj që keni nevojë të menjëhershme!**\n\nPër asistencë të shpejtë:\n📱 **WhatsApp:** +355 XX XXX XXXX (Përgjigje në 2-5 minuta)\n📧 **Email:** info@contenta.marketing\n\n💬 Një nga ekspertët tanë po ju kontakton tani! Ju lutemi prisni 1-2 minuta.\n\n⚡ Ndërkohë, më tregoni më shumë detaje për urgjencën tuaj që të ju ndihmojmë më shpejt.'
    }
    
    // Handle negative sentiment/problems
    if (negativeSentiment) {
      return '😟 **Na vjen shumë keq që hasët në problem!**\n\nJu lutem më tregoni më shumë detaje:\n• Cili është problemi specifik?\n• Kur filloi?\n• Çfarë mesazhi gabimi shfaqet (nëse ka)?\n\n🔧 Do të ju ndihmojmë ta zgjidhim menjëherë!\n\n📞 Për mbështetje teknike urgent:\n• WhatsApp: +355 XX XXX XXXX\n• Email: support@contenta.marketing\n\n👨‍💻 Ekipi ynë teknik është gati të ju ndihmojë 24/7!'
    }
    
    // Handle positive sentiment
    if (positiveSentiment) {
      return '😊 **Faleminderit shumë për fjalët e mira!**\n\n🌟 Na bën shumë të lumtur që jeni të kënaqur!\n\nSi mund t\'ju ndihmoj më tej sot?\n\n💡 Disa sugjerime:\n• Më shumë informacion për shërbimet\n• Kërkesë për ofertë personale\n• Rezervim konsultimi falas\n• Pyetje specifike për projektin tuaj\n\n🎁 Dhe mos harroni - kemi oferta speciale për klientët që kthehen!'
    }
    
    // Handle confused sentiment with extra help
    if (confusedSentiment) {
      return '🤔 **E kuptoj - Le ta sqarojmë bashkë!**\n\n📚 Më tregoni:\n• Çfarë informacioni ju duhet saktësisht?\n• Për cilin shërbim jeni kureshtar?\n• Ka ndonjë term që nuk e kuptoni?\n\n💡 **Mund të fillojmë me pyetje të thjeshta:**\n1. \"Sa kushton një website?\"\n2. \"Si funksionon social media management?\"\n3. \"Sa kohë zgjat të krijosh një logo?\"\n\nOSE mund të flisni direkt me një ekspert human! 👨‍💻\n\nÇfarë dëshironi të sqaroni së pari?'
    }
    
    // Handle interested sentiment with action-oriented response
    if (interestedSentiment) {
      return '🎯 **Shkëlqyeshëm! Ju jeni në vendin e duhur!**\n\nLe të fillojmë:\n\n**1️⃣ Më tregoni më shumë:**\n• Çfarë lloj biznesi keni?\n• Çfarë shërbimi ju intereson më shumë?\n• Keni ndonjë deadline apo buxhet në mendje?\n\n**2️⃣ OSE zgjidhni nga këto:**\n🌐 Website Development\n📱 Social Media Management\n🎨 Branding & Logo Design\n📊 SEO & Google Ads\n\n**3️⃣ Dëshironi konsultim FALAS?**\n📅 Mund të rezervoni një takim 30-minutësh\n📞 WhatsApp: +355 XX XXX XXXX (përgjigje në 5 min)\n\nÇfarë ju intereson më shumë?'
    }
    
    // Greetings
    if (msg.match(/\b(hi|hello|hey|përshëndetje|tungjatjeta|ckemi|hej|hallo)\b/i)) {
      return '👋 Përshëndetje! Unë jam asistenti virtual i Contenta Marketing. Jam këtu për t\'ju ndihmuar! Si mund t\'ju asistoj sot?\n\n💡 Mund të më pyesni për:\n• Shërbimet tona\n• Çmimet dhe paketat\n• Projektet që kemi realizuar\n• Procesin e punës\n• Çdo gjë tjetër që ju intereson!'
    }
    
    // Farewell
    if (msg.match(/\b(bye|goodbye|mirupafshim|faleminderit|flm|thanks|thank you)\b/i)) {
      return '👋 Faleminderit që na kontaktuat! Nëse keni pyetje të tjera, jam gjithmonë këtu. Ju urojmë një ditë të mbarë! ✨\n\nPër kontakt të menjëhershëm:\n📧 info@contenta.marketing\n📱 WhatsApp: +355 XX XXX XXXX'
    }
    
    // How are you
    if (msg.match(/\b(si je|how are you|ckemi|a je mire)\b/i)) {
      return '😊 Faleminderit që po më pyet! Jam në formë të shkëlqyer dhe gati t\'ju ndihmoj me çdo pyetje rreth shërbimeve tona!\n\n🚀 Si mund t\'ju asistoj sot? Mund të flasim për:\n• Website Development\n• Social Media Management\n• Branding & Design\n• SEO & Google Ads'
    }
    
    // General questions about services
    if (msg.match(/\b(shërbime|services|çfarë bëni|what do you do|oferta)\b/i)) {
      return '🌟 Contenta Marketing Agency ofron shërbime të plota dixhitale:\n\n🌐 **Web Development**\n   • Website korporativ profesional\n   • E-commerce platform\n   • Web aplikacione custom\n   • Dizajn modern & responsive\n\n📱 **Social Media Management**\n   • Strategji & planing editorial\n   • Content creation profesional\n   • Community management\n   • Ads campaigns\n\n🎨 **Branding & Design**\n   • Logo & identitet vizual\n   • Materiale marketingu\n   • Branding guidelines\n\n📊 **SEO & Digital Ads**\n   • Optimizim për Google\n   • Google Ads campaigns\n   • Facebook & Instagram ads\n\nÇfarë shërbimi ju intereson më shumë?'
    }
    
    // About the company
    if (msg.match(/\b(kush jeni|who are you|about|rreth|kompani|agjenci)\b/i)) {
      return '🏢 **Contenta Marketing Agency**\n\nJemi një agjenci e specializuar në marketing dixhital dhe web development, me mision t\'ju ndihmojmë të rritni prezencën tuaj online dhe të arrini rezultate konkrete!\n\n✨ **Përse ne?**\n• 100+ klientë të kënaqur\n• Ekip ekspertësh të certifikuar\n• Rezultate të matshme\n• Mbështetje 24/7\n• Çmime konkurruese\n\n🎯 **Specializimet tona:**\n• Digital Strategy\n• Web Development\n• Social Media Marketing\n• Branding & Design\n• SEO & SEM\n\nA dëshironi të mësoni më shumë për një shërbim specifik?'
    }
    
    // Pricing questions
    if (msg.match(/\b(çmim|price|cost|kosto|buxhet|paketa|sa kushton)\b/i)) {
      return '💰 **Çmimet tona janë transparente dhe konkurruese!**\n\n🌐 **Website Development**\n   • Website Bazë: 300€ - 600€\n   • Website Profesional: 600€ - 1,200€\n   • E-commerce: 1,200€ - 2,500€\n   • Custom Solutions: Sipas projektit\n\n📱 **Social Media Management**\n   • Paketa Basic: 150€/muaj (15 postime)\n   • Paketa Standard: 250€/muaj (25 postime)\n   • Paketa Premium: 400€/muaj (40 postime + ads)\n\n🎨 **Branding Komplet**\n   • Logo Design: 200€ - 500€\n   • Branding Komplet: 500€ - 1,500€\n\n📊 **SEO & Ads**\n   • SEO Optimization: 200€ - 400€/muaj\n   • Google Ads Management: 300€ - 800€/muaj\n   • Social Media Ads: 200€ - 600€/muaj\n\n🎁 **OFERTË SPECIALE:** -15% për kontratat 6+ mujore!\n\nDëshironi një ofertë të personalizuar për projektin tuaj?'
    }
    
    // Website specific
    if (msg.match(/\b(website|web|site|faqe|ueb|web development|dizajn web)\b/i)) {
      return '🌐 **Website Development - Krijojmë Përvojë Dixhitale Unike!**\n\n✅ **Çfarë përfshijmë:**\n• Dizajn modern, elegant & unik\n• 100% Responsive (desktop, tablet, mobile)\n• SEO optimized (Google friendly)\n• Shpejtësi e lartë (PageSpeed 90+)\n• SSL & Siguri e avancuar\n• CMS i lehtë për përdorim\n• Hosting 1 vit FALAS\n• Email profesional\n• Google Analytics integration\n• Mirëmbajtje 3 mujore FALAS\n\n💼 **Llojet e website-ve:**\n• Korporativ/Prezantim\n• E-commerce/Dyqan Online\n• Portfolio/Galeri\n• Blog & Magazine\n• Landing Pages\n• Web Aplikacione\n\n⏱️ **Afatet:**\n• Website bazë: 1-2 javë\n• Website profesional: 2-4 javë\n• E-commerce: 4-6 javë\n\n🎯 **Shembuj punimesh:** www.contenta.al/portfolio\n\nA keni një ide specifike për website-in tuaj?'
    }
    
    // Social media specific
    if (msg.match(/\b(social media|instagram|facebook|tiktok|rrjete sociale|postime)\b/i)) {
      return '📱 **Social Media Management - Rritni Prezencën Tuaj Online!**\n\n📈 **Shërbimet tona:**\n\n🎨 **Content Creation:**\n• Design grafik profesional\n• Video editing & Reels\n• Copywriting në shqip/anglisht\n• Stories & Highlights\n• Carousel posts\n• Branding konsistent\n\n📊 **Management & Strategy:**\n• Kalendar editorial mujor\n• Posting të rregullt\n• Hashtag research & optimization\n• Competitor analysis\n• Target audience research\n\n💬 **Community Management:**\n• Përgjigje në komente/mesazhe\n• Engagement me followers\n• Crisis management\n• Reputation monitoring\n\n📉 **Analytics & Reporting:**\n• Raportim mujor i detajuar\n• Growth tracking\n• Insights & rekomandime\n• A/B testing\n\n🎯 **Platformat:**\nInstagram • Facebook • TikTok • LinkedIn • YouTube • Pinterest\n\n💡 **Rezultatet:** Klientët tanë kanë parë rritje mesatare:\n• +200% në reach\n• +150% në engagement\n• +300% në website traffic\n\nA dëshironi të fillojmë strategjinë tuaj?'
    }
    
    // SEO & Ads
    if (msg.match(/\b(seo|google|ads|reklamim|reklama|marketing|optimizim)\b/i)) {
      return '📊 **SEO & Digital Advertising - Rritje e Garantuar!**\n\n🔍 **SEO (Search Engine Optimization):**\n• Keyword research i detajuar\n• On-page optimization\n• Technical SEO audit\n• Content optimization\n• Link building strategjik\n• Local SEO & Google My Business\n• Competitor analysis\n• Monthly reporting\n\n📈 **Rezultate SEO:**\n• Pozicionim në faqen 1 të Google\n• Rritje 200-500% në traffic organik\n• Më shumë leads & conversions\n\n💰 **Google Ads (PPC):**\n• Search campaigns\n• Display & banner ads\n• Shopping ads (për e-commerce)\n• YouTube advertising\n• Remarketing strategjik\n• Conversion tracking\n• ROI optimization\n\n📱 **Social Media Ads:**\n• Facebook & Instagram ads\n• TikTok advertising\n• LinkedIn campaigns\n• A/B testing i vazhdueshëm\n• Lookalike & custom audiences\n• Retargeting campaigns\n\n💡 **Statistikat tona:**\n• ROI mesatar: 400-600%\n• Cost per click: 40-60% më i ulët se konkurrenca\n• Conversion rate: +250%\n\nDëshironi një audit FALAS të website-it tuaj?'
    }
    
    // Process/How it works
    if (msg.match(/\b(proces|process|si funksionon|how does it work|hapa|steps|filloj)\b/i)) {
      return '🎯 **Procesi Ynë - Nga Ideja në Sukses!**\n\n**1️⃣ KONSULTIM FALAS (30-60 min)**\n   • Bisedë për biznesin tuaj\n   • Analizë e nevojave\n   • Diskutim i objektivave\n   • Rekomandime fillestare\n\n**2️⃣ PROPOZIM & OFERTË (2-3 ditë)**\n   • Strategji e detajuar\n   • Plan pune & timeline\n   • Ofertë transparente\n   • Prezantim i projektit\n\n**3️⃣ KONTRATË & FILLIM**\n   • Nënshkrimi i kontratës\n   • Pagesa e avancës (50%)\n   • Kickoff meeting\n   • Setup i projektit\n\n**4️⃣ ZHVILLIM (2-6 javë)**\n   • Design & prototyping\n   • Development/Implementation\n   • Feedback i vazhdueshëm\n   • Revizione (të pakufizuara)\n\n**5️⃣ TESTIM & QA**\n   • Quality assurance\n   • Bug fixing\n   • Cross-platform testing\n   • Performance optimization\n\n**6️⃣ LANSIM 🚀**\n   • Go-live i projektit\n   • Trajnim për stafin\n   • Dokumentacion i plotë\n   • Pagesa finale (50%)\n\n**7️⃣ MBËSHTETJE**\n   • Support 24/7\n   • Mirëmbajtje e vazhdueshme\n   • Updates & improvements\n   • Raportim mujor\n\n✨ **Komunikim transparent në çdo hap!**\n\nGati të filloni projektin tuaj?'
    }
    
    // Portfolio/Projects
    if (msg.match(/\b(portfolio|projekt|projekte|punë|shembuj|example|case study)\b/i)) {
      return '🏆 **Portfolio & Projekte të Suksesshme**\n\n📊 **Statistikat tona:**\n✅ 100+ Website të zhvilluara\n✅ 50+ Biznese me branding të plotë\n✅ 80+ Llogari sociale të menaxhuara\n✅ 200+ Kampanje marketingu\n✅ 95% shkallë kënaqësie klientësh\n✅ 4.9/5 rating mesatar\n\n🌟 **Industri që mbulojmë:**\n• Restorant & Horeca\n• Real Estate & Ndërtim\n• Fashion & Beauty\n• Teknologji & IT\n• Shëndetësi & Wellness\n• Edukim & Trajnime\n• E-commerce & Retail\n• Automotive\n• Legal Services\n\n📈 **Rezultate të matshme:**\n• +350% rritje në website traffic\n• +250% më shumë leads\n• +180% rritje në shitje online\n• +400% engagement në social media\n\n🎯 **Projekte të fundit:**\n1. E-commerce platform për fashion brand\n2. Website korporativ për kompani ndërtimi\n3. Social media campaign për restorant\n4. Rebranding komplet për tech startup\n\n👀 **Shihni punën tonë:** www.contenta.al/portfolio\n\nA dëshironi të shihni një case study specifik?'
    }
    
    // Contact info
    if (msg.match(/\b(kontakt|contact|telefon|phone|email|adres|address|lokacion)\b/i)) {
      return '📞 **Na Kontaktoni - Jemi Këtu për Ju!**\n\n📧 **Email:**\ninfo@contenta.marketing\nsupport@contenta.marketing\n\n📱 **Telefon & WhatsApp:**\n+355 XX XXX XXXX\n+355 XX XXX XXXX\n\n📍 **Adresa:**\nRruga Dëshmorët e 4 Shkurtit\nPallati [X], Kati [X]\nTiranë 1001, Shqipëri\n\n🕐 **Orari i Punës:**\nE Hënë - E Premte: 09:00 - 18:00\nE Shtunë: 10:00 - 14:00\nE Diel: Mbyllur\n\n🌐 **Online:**\nWebsite: www.contenta.al\nInstagram: @contenta.marketing\nFacebook: Contenta Marketing Agency\nLinkedIn: Contenta Marketing\n\n💬 **Live Chat:** Disponueshëm 24/7 përmes këtij chat-i!\n\n📅 **Rezervo takim:** calendly.com/contenta\n\nSi mund t\'ju ndihmoj më tej?'
    }
    
    // Help/Don't know what to ask
    if (msg.match(/\b(ndihmë|help|assist|si|what|çfarë|mund)\b/i)) {
      return '💡 **Si mund t\'ju ndihmoj?**\n\nJam këtu për t\'ju dhënë informacione për:\n\n1️⃣ **Shërbimet:** Web, Social Media, Branding, SEO\n2️⃣ **Çmimet:** Paketa dhe oferta speciale\n3️⃣ **Portfolio:** Projektet që kemi realizuar\n4️⃣ **Procesi:** Si punojmë me klientët\n5️⃣ **Kohëzgjatja:** Sa zgjat një projekt\n6️⃣ **Kontakt:** Telefon, email, adresa\n\n🎯 **Ose më pyesni diçka specifike!**\n\nPër shembull:\n• \"Sa kushton një website?\"\n• \"Si funksionon social media management?\"\n• \"Keni bërë website për restorant?\"\n• \"Dua një ofertë për branding\"\n\nÇfarë ju intereson më shumë?'
    }
    
    // Budget/Money questions
    if (msg.match(/\b(buxhet|money|para|affordable|lirë|cheap|sa kam nevojë|sa duhet|payment|pagesë)\b/i)) {
      return '💰 **Buxheti & Opsione Fleksibël**\n\nE kuptojmë që buxheti është i rëndësishëm!\n\n✅ **Opsione për çdo buxhet:**\n• Paketa fillestare: Nga 150€\n• Zgjidhje të personalizuara\n• Plane pagese mujore\n• Skonto për kontrata afatgjata (-15% për 6+ muaj)\n\n📊 **Raport cilësi-çmim i shkëlqyer!**\n\n💳 **Metoda pagese:**\n• Transfer bankar • Cash • Kartë krediti • Këste\n\n🎯 **Konsultimi është 100% FALAS!**\n\nSa është buxheti juaj i përafërt?'
    }
    
    // Timeline/Deadline questions
    if (msg.match(/\b(sa kohë|deadline|shpejt|when|kur|afat|time|sa zgjat|koha)\b/i)) {
      return '⏱️ **Afatet & Kohëzgjatja**\n\n🚀 **Kohë realizimi:**\n• Landing Page: 3-5 ditë ⚡\n• Website Bazë: 1-2 javë\n• Website Profesional: 2-4 javë\n• E-commerce: 4-6 javë\n\n**Social Media:** Fillimi brenda 1 javë\n**Branding:** 2-3 javë\n**SEO:** Rezultate në 2-4 javë\n\n⚡ **RUSH SERVICE:** +50% më shpejt (+20% kosto)\n\nKeni një deadline specifik?'
    }
    
    // Comparison questions
    if (msg.match(/\b(pse ju|why you|përse|difference|ndryshimi|competitor|më mirë|best)\b/i)) {
      return '🏆 **Pse Contenta Marketing?**\n\n✨ **Dallimi ynë:**\n✅ Rezultate të garantuara\n✅ Transparencë 100%\n✅ Ekip i certifikuar (Google & Meta Partners)\n✅ Teknologji e avancuar (AI-powered)\n✅ 30% më lirë se konkurrenca\n✅ Mbështetje 24/7\n\n📊 **Rezultate:**\n• 95% shkallë kënaqësie\n• 100+ klientë të lumtur\n• ROI mesatar 400-600%\n\nA dëshironi të flasim me një nga klientët tanë?'
    }
    
    // Industry-specific: Restaurant/Horeca
    if (msg.match(/\b(restorant|restaurant|bar|kafe|hotel|horeca|food)\b/i)) {
      return '🍽️ **Zgjidhje për Restorant & Horeca**\n\n📱 **Shërbimet:**\n• Food photography profesionale\n• Website me rezervime online\n• Social media management\n• Google My Business optimization\n• Review management\n\n💡 **Paketa Speciale: €399/muaj**\n✅ Website + Social media + Google Ads\n\n🎯 **Rezultate:** +300% rezervime online!\n\nKeni restorant apo bar? Më tregoni!'
    }
    
    // Industry-specific: Real Estate
    if (msg.match(/\b(real estate|pasuri|ndërtim|construction|apartament|building)\b/i)) {
      return '🏗️ **Zgjidhje për Real Estate**\n\n🌐 **Shërbimet:**\n• Website me property listings\n• Virtual tours 360°\n• CRM për leads\n• Drone photography\n• Facebook & Google Ads\n\n💼 **Paketa: €599/muaj**\n✅ Website + CRM + Ads + Drone photos\n\n🎯 **Rezultate:** +400% leads!\n\nPunoni në pasuri? Le të flasim!'
    }
    
    // Industry-specific: Fashion/Beauty
    if (msg.match(/\b(fashion|mode|beauty|bukuri|salon|clothes|veshje)\b/i)) {
      return '👗 **Zgjidhje për Fashion & Beauty**\n\n🛍️ **Shërbimet:**\n• E-commerce platform\n• Professional photography\n• Social media content\n• Instagram Shopping\n• Influencer collaborations\n\n✨ **Paketa: €499/muaj**\n\n🎯 **Success story:** +500% online sales!\n\nJeni në mode/bukuri? Le të ndërtojmë brand-in tuaj!'
    }
    
    // Technical questions
    if (msg.match(/\b(responsive|mobile|desktop|design|technology|teknologji|hosting|seo optimization)\b/i)) {
      return '💻 **Aspekte Teknike**\n\n📱 **Design & Tech:**\n• 100% responsive (mobile, tablet, desktop)\n• Fast loading (< 2 seconds)\n• React / Next.js / Vue.js\n• SSL & Security maksimale\n\n🚀 **Performance:**\n• PageSpeed 90+\n• Core Web Vitals optimized\n• CDN integration\n\n☁️ **Hosting:**\n• 99.9% uptime guarantee\n• Daily backups\n• Free SSL included\n\nKeni pyetje teknike specifike?'
    }
    
    // Support questions
    if (msg.match(/\b(support|mbështetje|maintenance|mirëmbajtje|update)\b/i)) {
      return '🛠️ **Mbështetje & Ndihmë**\n\n📞 **Kanalet:**\n• WhatsApp: +355 XX XXX XXXX (< 30 min)\n• Email: support@contenta.marketing (< 24h)\n• Live Chat: 24/7\n\n✅ **Përfshin:**\n• Bug fixes falas\n• Technical support\n• Content updates\n• Security patches\n\n💡 **SLA:**\n• Critical: Fix në < 4 orë\n• Normal: Fix në < 24 orë\n\nÇfarë problemi keni?'
    }
    
    // Consultation/Meeting requests
    if (msg.match(/\b(konsultim|consultation|takim|meeting|bisedoj|flas|rezervo)\b/i)) {
      return '📅 **Rezervo Konsultim FALAS**\n\n🎯 **30-60 minuta diskutim për:**\n• Analizë të nevojave tuaja\n• Rekomandime strategjike\n• Plan pune & timeline\n• Ofertë transparente\n\n📞 **Mënyrat:**\n• Video call (Zoom/Google Meet)\n• Telefon\n• Takim fizik në zyrën tonë\n• WhatsApp video\n\n📅 **Disponueshmëri:**\n• E Hënë - E Premte: 09:00-18:00\n• E Shtunë: 10:00-14:00\n\n🔗 **Rezervo tani:**\ncalendly.com/contenta\n\nOSE më tregoni kur jeni i disponueshëm dhe unë rregulloj takimin!'
    }
    
    // Contract/Legal questions
    if (msg.match(/\b(kontratë|contract|legal|ligjor|garanci|warranty|terms)\b/i)) {
      return '📄 **Kontrata & Garanci**\n\n✅ **Çfarë përfshin kontrata:**\n• Scope i qartë i punës\n• Timeline i detajuar\n• Çmim transparent (pa kosto të fshehura)\n• Kushtet e pagesës\n• Revision policy\n• Terms & Conditions\n\n🛡️ **Garancionet tona:**\n• 100% kënaqësi ose rimbursim\n• Rishikime të pakufizuara\n• Mirëmbajtje 3 muaj FALAS\n• Support 24/7\n• Hosting 1 vit FALAS\n\n📋 **Procesi:**\n1. Konsultim & Propozim\n2. Nënshkrimi i kontratës\n3. Avancë 50%\n4. Fillimi i punës\n5. Feedback & revizione\n6. Dorëzimi & pagesa finale 50%\n\n🔒 **Konfidencialitet:**\n• NDA (Non-Disclosure Agreement)\n• GDPR compliant\n• Data security maksimale\n\nA keni pyetje specifike ligjore?'
    }
    
    // Competition/Alternatives questions
    if (msg.match(/\b(Fiverr|Upwork|freelancer|tjetër agjenci|alternative)\b/i)) {
      return '🤔 **Contenta vs Alternatives**\n\n**🌍 Fiverr/Upwork:**\n❌ Cilësi e dyshimtë\n❌ Komunikim i vështirë\n❌ Asnjë garanci\n❌ Probleme me revizione\n\n**✅ Contenta Marketing:**\n✅ Ekip vendas (shqiptar)\n✅ Takim ballë për ballë\n✅ Komunikim 24/7 në gjuhën tuaj\n✅ Garanci 100% kënaqësie\n✅ Mbështetje afatgjatë\n\n**🏢 Agjenci të tjera:**\n• Ne jemi 30% më lirë\n• Kohë realizimi më e shpejtë\n• Transparencë më e madhe\n• Rezultate të garantuara\n\n💡 **Përse të zgjidhni ne:**\n• 100+ klientë të kënaqur\n• Portfolio i pasur\n• Çmime konkurruese\n• Ekspertizë e vërtetë\n• Jo vetëm premtime, rezultate!\n\nA dëshironi të krahasojmë një ofertë që keni marrë?'
    }
    
    // Starting a business/Startup questions
    if (msg.match(/\b(startup|start|filloj|biznes i ri|new business|po hap|launch)\b/i)) {
      return '🚀 **Zgjidhje për Startup & Biznese të Reja**\n\n🎯 **Paketa "Startup Launch" - €899**\n\n✅ **Përfshin gjithçka për të filluar:**\n• Logo & Brand Identity\n• Website profesional\n• Social Media setup (3 platforma)\n• Google My Business\n• Email marketing setup\n• Business cards dizajn\n• 1 muaj social media management FALAS\n\n📊 **Bonus për startup:**\n• Konsultim strategjik\n• Business plan assistance\n• Pitch deck design\n• Investor presentation\n\n💰 **Opsione pagese:**\n• 3 këste pa interes\n• 0€ avancë për studentë/të rinj\n• Barter/trade për equity (raste të veçanta)\n\n🎁 **OFERTË SPECIALE:**\n-20% për studentë & entrepreneurs nën 25 vjeç!\n\n📈 **Success stories:**\n• Tech Startup A: €50K funding raised\n• Fashion Brand B: 10K followers në 2 muaj\n• Food Delivery C: 500+ orders muaji i parë\n\n💡 **Free resources:**\n• Startup checklist\n• Marketing template\n• Launch strategy guide\n\nÇfarë biznesi po hapni? Le të planifikojmë së bashku!'
    }
    
    // Random/Unrecognized - Smart fallback
    return `🤖 Faleminderit për pyetjen! Nuk jam 100% i sigurt se çfarë po kërkoni, por jam këtu për t'ju ndihmuar!\n\n💡 **Mund të më pyesni për:**\n\n📌 **Shërbimet tona:**\n• Website Development\n• Social Media Management\n• Branding & Design\n• SEO & Google Ads\n\n📌 **Informacione të tjera:**\n• Çmimet dhe paketat\n• Procesi i punës\n• Portfolio dhe shembuj\n• Kontakte\n\n🎯 **Ose thjesht më tregoni:**\nÇfarë lloj biznesi keni dhe si mund t'ju ndihmojmë?\n\n💬 Nëse preferoni, mund të flisni direkt me një nga ekspertët tanë që janë online! ✨`
  }

  const handleSendMessage = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (inputMessage.trim() && sessionId) {
      try {
        const userMsg = inputMessage
        
        // Add user message to Firestore
        await addDoc(collection(db, 'chatMessages'), {
          sessionId,
          sender: 'user',
          senderName: userName,
          text: userMsg,
          timestamp: serverTimestamp(),
          read: false
        })
        
        setInputMessage('')

        // If talking to human, don't auto-respond
        if (talkingToHuman) return

        // Bot auto-response
        setIsTyping(true)
        setTimeout(async () => {
          const botResponse = getBotResponse(userMsg)
          await addDoc(collection(db, 'chatMessages'), {
            sessionId,
            sender: 'bot',
            senderName: 'AI Asistent',
            text: botResponse,
            timestamp: serverTimestamp(),
            read: false
          })
          setIsTyping(false)
        }, 1500 + Math.random() * 1000) // Random delay 1.5-2.5s for natural feel
        
      } catch (error) {
        console.error('Error sending message:', error)
        alert('Mesazhi nuk u dërgua. Provoni përsëri.')
      }
    }
  }

  const handleAcceptHandoff = async () => {
    setTalkingToHuman(true)
    setAgentStatus('online')
    
    try {
      // Update session to indicate human handoff requested
      const sessionsRef = collection(db, 'chatSessions')
      const q = query(sessionsRef, where('sessionId', '==', sessionId))
      const snapshot = await getDocs(q)
      
      if (!snapshot.empty) {
        await updateDoc(doc(db, 'chatSessions', snapshot.docs[0].id), {
          assignedTo: 'human',
          handoffRequested: true,
          handoffAt: serverTimestamp()
        })
      }

      // Bot confirmation message
      await addDoc(collection(db, 'chatMessages'), {
        sessionId,
        sender: 'bot',
        senderName: 'AI Asistent',
        text: 'Perfekt! Ju kam lidhur me stafin tonë. Një moment të lutem... 👨‍💼',
        timestamp: serverTimestamp(),
        read: false
      })
    } catch (error) {
      console.error('Error during handoff:', error)
    }
  }

  const handleDeclineHandoff = async () => {
    try {
      await addDoc(collection(db, 'chatMessages'), {
        sessionId,
        sender: 'bot',
        senderName: 'AI Asistent',
        text: 'Asnjë problem! Vazhdoj të ju ndihmoj unë. Çfarë tjetër dëshironi të dini? 😊',
        timestamp: serverTimestamp(),
        read: false
      })
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleEndSession = async () => {
    if (window.confirm('Dëshiron ta mbyllësh bisedën? Mesazhet do të ruhen për ju.')) {
      try {
        // Update session status to closed
        const sessionsRef = collection(db, 'chatSessions')
        const q = query(sessionsRef, where('sessionId', '==', sessionId))
        const snapshot = await getDocs(q)
        
        if (!snapshot.empty) {
          await updateDoc(doc(db, 'chatSessions', snapshot.docs[0].id), {
            status: 'closed',
            closedAt: serverTimestamp(),
            closedBy: 'client'
          })
        }

        // Send goodbye message
        await addDoc(collection(db, 'chatMessages'), {
          sessionId,
          sender: 'bot',
          senderName: 'Sistemi',
          text: 'Faleminderit që biseduat me ne! Mund të na kontaktoni përsëri kur të dëshironi. Keni një ditë të mrekullueshme! 👋',
          timestamp: serverTimestamp()
        })

        // Reset to contact form
        setShowContactForm(true)
        setSessionId(null)
        setMessages([])
        setTalkingToHuman(false)
        setIsHandoffOffered(false)
      } catch (error) {
        console.error('Error ending session:', error)
      }
    }
  }

  const quickReplies = [
    'Dua një website të ri',
    'Kam nevojë për social media',
    'Si funksionon procesi?',
    'Cilat janë çmimet?'
  ]

  const handleQuickReply = (reply) => {
    setInputMessage(reply)
  }

  if (showContactForm) {
    return (
      <>
        <SEO
          title="Konsultim Falas Live - Chat me Ekspertët"
          description="Filloni një bisedë live me ekspertët tanë të marketingut dixhital. Jemi online dhe gati për t'ju ndihmuar me biznesin tuaj."
          keywords="live chat, konsultim falas, bisedë online, mbështetje marketing, contenta chat"
        />
        
        <div className="min-h-screen bg-dark pt-20">
          <div className="container mx-auto px-4 py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto"
            >
              {/* Header */}
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-gold rounded-full mb-6 shadow-glow"
                >
                  <MessageCircle className="w-10 h-10 text-white" />
                </motion.div>
                <h1 className="text-4xl md:text-5xl font-display font-bold text-gradient-gold mb-4">
                  Konsultim Falas Live
                </h1>
                <p className="text-xl text-beige-200 mb-2">
                  Bisedoni me ekspertët tanë në kohë reale
                </p>
                <div className="flex items-center justify-center space-x-2 text-green-400">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">Jemi Online Tani</span>
                </div>
              </div>

              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-2xl p-8 shadow-gold"
              >
                <h2 className="text-2xl font-display font-bold text-gold-400 mb-6">
                  Filloni Bisedën
                </h2>
                
                <form onSubmit={handleStartChat} className="space-y-6">
                  <div>
                    <label className="block text-beige-200 font-medium mb-2">
                      Emri Juaj *
                    </label>
                    <input
                      type="text"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      placeholder="Shkruani emrin tuaj"
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-beige-200 font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={userEmail}
                      onChange={(e) => setUserEmail(e.target.value)}
                      placeholder="email@example.com"
                      required
                      className="w-full px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center space-x-2 py-4"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Fillo Chat-in</span>
                  </button>
                </form>

                {/* Quick Info */}
                <div className="mt-8 pt-6 border-t border-gold-500/20">
                  <p className="text-beige-300 text-sm text-center mb-4">
                    Ose na kontaktoni direkt:
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    <a href="https://wa.me/355123456789" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-3 bg-dark/50 rounded-lg hover:bg-dark transition-colors group">
                      <Phone className="w-5 h-5 text-gold-400 mb-1 group-hover:scale-110 transition-transform" />
                      <span className="text-xs text-beige-300">WhatsApp</span>
                    </a>
                    <a href="mailto:info@contenta.marketing" className="flex flex-col items-center p-3 bg-dark/50 rounded-lg hover:bg-dark transition-colors group">
                      <Mail className="w-5 h-5 text-gold-400 mb-1 group-hover:scale-110 transition-transform" />
                      <span className="text-xs text-beige-300">Email</span>
                    </a>
                    <a href="tel:+355123456789" className="flex flex-col items-center p-3 bg-dark/50 rounded-lg hover:bg-dark transition-colors group">
                      <Video className="w-5 h-5 text-gold-400 mb-1 group-hover:scale-110 transition-transform" />
                      <span className="text-xs text-beige-300">Video Call</span>
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Features */}
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                {[
                  { icon: Clock, text: 'Përgjigje të Shpejta', sub: 'Brenda 2-3 min' },
                  { icon: User, text: 'Ekspertë Realë', sub: 'Jo robotë' },
                  { icon: CheckCheck, text: '100% Falas', sub: 'Pa detyrime' }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="text-center p-6 bg-bronze-900/20 rounded-xl border border-gold-500/10"
                  >
                    <feature.icon className="w-8 h-8 text-gold-400 mx-auto mb-3" />
                    <h3 className="font-semibold text-cream mb-1">{feature.text}</h3>
                    <p className="text-sm text-beige-400">{feature.sub}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title="Live Chat - Contenta Marketing"
        description="Bisedoni live me ekspertët tanë të marketingut dixhital"
        keywords="live chat, mbështetje, konsultim"
      />
      
      <div className="min-h-screen bg-dark pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Chat Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-gold rounded-t-2xl p-6 shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                      {talkingToHuman ? (
                        <User className="w-8 h-8 text-gold-500" />
                      ) : (
                        <Bot className="w-8 h-8 text-blue-500" />
                      )}
                    </div>
                    <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
                      agentStatus === 'online' ? 'bg-green-400' : 
                      agentStatus === 'bot' ? 'bg-blue-400' : 'bg-gray-400'
                    }`}></div>
                  </div>
                  <div>
                    <h2 className="text-white font-display font-bold text-lg">
                      {talkingToHuman ? 'Contenta Staff' : 'AI Asistent'}
                    </h2>
                    <p className="text-white/80 text-sm">
                      {talkingToHuman ? 'Stafi Online' : 'Bot Automatik'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-white/80 text-sm">
                    {userName}
                  </div>
                  {sessionId && (
                    <button
                      onClick={handleEndSession}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 rounded-lg text-white text-sm font-medium transition-all duration-200 flex items-center space-x-2"
                      title="Mbyll bisedën"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                      <span>Mbyll</span>
                    </button>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Messages Container */}
            <motion.div
              ref={chatContainerRef}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-dark/50 backdrop-blur-sm border-x border-gold-500/20 h-[500px] overflow-y-auto p-6 space-y-4"
              style={{ scrollBehavior: 'smooth' }}
            >
              <AnimatePresence>
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${message.sender === 'user' ? 'order-2' : 'order-1'}`}>
                      {/* Show sender name for bot/staff */}
                      {message.sender !== 'user' && (
                        <p className="text-xs text-beige-400 mb-1 ml-2 flex items-center">
                          {message.sender === 'bot' && <Bot className="w-3 h-3 mr-1" />}
                          {message.senderName}
                        </p>
                      )}
                      <div className={`rounded-2xl p-4 ${
                        message.sender === 'user'
                          ? 'bg-gradient-gold text-white'
                          : message.sender === 'bot'
                          ? 'bg-blue-900/50 border border-blue-500/30 text-cream'
                          : 'bg-bronze-900/50 border border-gold-500/20 text-cream'
                      }`}>
                        <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
                      </div>
                      
                      {/* Handoff buttons */}
                      {message.isHandoffOffer && !talkingToHuman && (
                        <div className="flex space-x-2 mt-2">
                          <button
                            onClick={handleAcceptHandoff}
                            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors"
                          >
                            ✓ Po, dua të flas
                          </button>
                          <button
                            onClick={handleDeclineHandoff}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded-lg transition-colors"
                          >
                            × Jo, faleminderit
                          </button>
                        </div>
                      )}
                      
                      <div className={`flex items-center space-x-1 mt-1 text-xs text-beige-400 ${
                        message.sender === 'user' ? 'justify-end' : 'justify-start'
                      }`}>
                        <span>{message.time}</span>
                        {message.sender === 'user' && (
                          <CheckCheck className="w-3 h-3 text-blue-400" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Typing Indicator */}
              <AnimatePresence>
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex justify-start"
                  >
                    <div className="bg-bronze-900/50 border border-gold-500/20 rounded-2xl p-4">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gold-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gold-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gold-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div ref={messagesEndRef} />
            </motion.div>

            {/* Quick Replies */}
            {messages.length <= 2 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-dark/30 border-x border-gold-500/20 px-6 py-3"
              >
                <p className="text-xs text-beige-400 mb-2">Përgjigje të Shpejta:</p>
                <div className="flex flex-wrap gap-2">
                  {quickReplies.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickReply(reply)}
                      className="px-3 py-1.5 bg-bronze-900/50 hover:bg-bronze-900 border border-gold-500/20 rounded-full text-xs text-beige-200 transition-colors"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Input Area */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              onSubmit={handleSendMessage}
              className="bg-bronze-900/30 backdrop-blur-sm border border-gold-500/20 rounded-b-2xl p-4"
            >
              <div className="flex items-center space-x-3">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSendMessage(e)
                    }
                  }}
                  placeholder="Shkruani mesazhin tuaj këtu..."
                  className="flex-1 px-4 py-3 bg-dark/50 border border-gold-500/30 rounded-xl text-cream placeholder-beige-400 focus:border-gold-400 focus:ring-2 focus:ring-gold-500/20 transition-all outline-none"
                />
                <button
                  type="submit"
                  disabled={!inputMessage.trim()}
                  className="btn-primary p-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </>
  )
}

export default LiveChat
